/*
  Zoom lightbox for the TLPC product galleries.

  Markup: snippets/tlpc-lightbox.liquid (rendered once from layout/theme.liquid)
  Styles: assets/theme.css ("TLPC zoom lightbox" block)
  Opened by: assets/tlpc-gallery.js, from Flickity's staticClick

  Replaces the Swiper build of this lightbox. Two things it fixes beyond the
  library swap:

  - It opens on the image you clicked. The Swiper version passed the start index
    to `slideTo()` on a `loop: true` instance, which counts the duplicated
    slides Swiper injects around the real ones - so the first arrow press
    snapped back to slide 1 no matter where you started. Flickity's
    `initialIndex` is a real cell index and needs no loop bookkeeping.
  - It rebuilds rather than reuses. The Swiper version emptied
    `.swiper-wrapper` and called `update()`, which left the old slide count in
    place on a second open.

  Zoom is a plain scale transform on the active image (Swiper's zoom module was
  the only reason that library was in this path). Click or double-tap to toggle;
  the transform-origin follows the pointer so you magnify what you aimed at.
*/
(function () {
  'use strict';

  const ZOOMED_CLASS = 'is-zoomed';
  const OPEN_CLASS = 'is-open';

  const root = document.getElementById('tlpc-lightbox');
  if (!root) return;

  const track = root.querySelector('[data-tlpc-lightbox-track]');
  const closeButton = root.querySelector('[data-tlpc-lightbox-close]');
  let flkty = null;

  function teardown() {
    if (flkty) {
      flkty.destroy();
      flkty = null;
    }
    track.innerHTML = '';
  }

  function close() {
    if (!root.classList.contains(OPEN_CLASS)) return;

    root.classList.remove(OPEN_CLASS);
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('tlpc-lightbox-open');
    teardown();
  }

  // A scale transform on the <img>, not on the cell: Flickity owns the cell's
  // own transform, so scaling that would be undone on the next reposition.
  function toggleZoom(cell, event) {
    const img = cell.querySelector('img');
    if (!img) return;

    if (cell.classList.contains(ZOOMED_CLASS)) {
      cell.classList.remove(ZOOMED_CLASS);
      img.style.removeProperty('transform-origin');
      return;
    }

    const rect = img.getBoundingClientRect();
    if (rect.width && rect.height) {
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
    }
    cell.classList.add(ZOOMED_CLASS);
  }

  function open(images, startIndex) {
    if (!Array.isArray(images) || !images.length) return;
    if (typeof window.Flickity === 'undefined') {
      console.error('[tlpc-lightbox] window.Flickity is undefined - vendor.js or flickity-alias.js did not run.');
      return;
    }

    // Always start from a clean track; see the note about reuse at the top.
    teardown();

    const index = Math.min(Math.max(startIndex || 0, 0), images.length - 1);

    images.forEach((src, i) => {
      const cell = document.createElement('div');
      cell.className = 'tlpc-lightbox__cell';

      const img = document.createElement('img');
      img.className = 'tlpc-lightbox__img';
      img.src = src;
      img.alt = '';
      // The one the shopper is waiting on decodes at normal priority; the rest
      // are offscreen until they navigate to them.
      img.loading = i === index ? 'eager' : 'lazy';
      img.decoding = 'async';

      cell.appendChild(img);
      track.appendChild(cell);
    });

    root.classList.add(OPEN_CLASS);
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('tlpc-lightbox-open');

    // Constructed only now that the element is visible - Flickity measures
    // cells, and a display:none track measures as zero.
    flkty = new Flickity(track, {
      initialIndex: index,
      cellAlign: 'center',
      wrapAround: images.length > 1,
      prevNextButtons: images.length > 1,
      pageDots: false,
      adaptiveHeight: false,
      imagesLoaded: true,
      setGallerySize: false,
      // Dragging is switched off while an image is zoomed (see the staticClick
      // handler): at 2x, small pointer movements over the image would otherwise
      // read as slide drags and throw you onto the next photo mid-inspection.
      draggable: images.length > 1
    });

    flkty.on('staticClick', (event, pointer, cellElement) => {
      if (!cellElement) return;
      toggleZoom(cellElement, pointer);
      flkty.options.draggable = !cellElement.classList.contains(ZOOMED_CLASS) && images.length > 1;
      flkty.updateDraggable();
    });

    // Leaving a slide drops its zoom, so you never arrive at a new image
    // already magnified into a corner.
    flkty.on('select', () => {
      flkty.cells.forEach(({ element }) => {
        element.classList.remove(ZOOMED_CLASS);
        const img = element.querySelector('img');
        if (img) img.style.removeProperty('transform-origin');
      });
      flkty.options.draggable = images.length > 1;
      flkty.updateDraggable();
    });

    if (closeButton) closeButton.focus();
  }

  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-tlpc-lightbox-close]') || event.target === root ||
      event.target.classList.contains('tlpc-lightbox__bg')) {
      close();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!root.classList.contains(OPEN_CLASS)) return;

    if (event.key === 'Escape') {
      close();
    } else if (flkty && event.key === 'ArrowRight') {
      flkty.next();
    } else if (flkty && event.key === 'ArrowLeft') {
      flkty.previous();
    }
  });

  window.tlpcLightbox = { open, close };
})();
